import React, { useEffect, useState, useCallback, useRef } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { SectieKop, LIJST, KAART } from "@/components/rust/Rust";
import { LoadError, errMessage } from "@/components/LoadError";
import { cap } from "@/components/afspraken/afsprakenUtils";
import { AutoVeld } from "./AutoVeld";
import { OverlegAfspraak } from "./OverlegAfspraak";
import { vragenVoor } from "./overlegRegels";

const STATUS_TEKST = { bezig: "Opslaan…", ok: "Opgeslagen ✓", fout: "Niet opgeslagen" };
const samen = (m) => { const v = Object.values(m); return v.includes("fout") ? "fout" : v.includes("bezig") ? "bezig" : v.includes("ok") ? "ok" : null; };

// v19-B3: rechts het overleg van de gekozen afdeling voor de huidige week; eerdere weken alleen lezen.
export function OverlegPaneel({ afdelingId, versie, onActie, onNieuw, onGewijzigd }) {
  const [week, setWeek] = useState("");
  const [d, setD] = useState(null);
  const [fout, setFout] = useState("");
  const [statussen, setStatussen] = useState({});
  const seq = useRef(0);
  const laad = useCallback(() => {
    const mijn = ++seq.current;   // R3: alleen het antwoord op de laatste vraag telt
    api.get(`/afdelingen/${afdelingId}/overleg${week ? `?week=${week}` : ""}`)
      .then((r) => { if (mijn === seq.current && r.data.afdeling === afdelingId && (!week || r.data.week === week)) { setD(r.data); setFout(""); } })
      .catch((e) => { if (mijn === seq.current) setFout(errMessage(e)); });
  }, [afdelingId, week]);
  useEffect(() => { setD(null); laad(); }, [laad, versie]);
  useEffect(() => { setStatussen({}); }, [week]);
  const status = useCallback((id, s) => { setStatussen((m) => ({ ...m, [id]: s })); if (s === "ok") onGewijzigd(); }, [onGewijzigd]);

  if (fout) return <LoadError error={fout} onRetry={laad} testid="overleg-error" />;
  if (!d) return <div data-testid="overleg-laden" className={`${KAART} min-h-[70vh] md:min-h-0 p-10 text-center text-sm text-zinc-400`}>Laden…</div>;
  const pad = `/afdelingen/${afdelingId}/overleg`;
  const veldKey = `${afdelingId}-${d.week}`;
  const opslag = samen(statussen);
  const vragen = vragenVoor(d);
  const loopt = async (a) => {
    try { await api.post(`${pad}/besproken`, { week: d.week, afspraakId: a.id }); laad(); onGewijzigd(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };
  return (
    <div data-testid="overleg-paneel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h2 data-testid="overleg-titel" className="font-heading text-2xl font-black tracking-tight text-zinc-900">Weekoverleg {d.naam}</h2>
          <p className="text-sm text-zinc-500">{d.datumLabel} · {d.leden.length ? d.leden.map(cap).join(", ") : "nog geen account"}{d.door.length ? ` · door ${d.door.map(cap).join(" en ")}` : ""}</p>
        </div>
        <div className="flex items-center gap-3">
          {opslag && <span data-testid="overleg-opgeslagen" className={`text-xs font-semibold ${opslag === "fout" ? "text-rose-600" : "text-emerald-700"}`}>{STATUS_TEKST[opslag]}</span>}
          <select data-testid="overleg-week" aria-label="Week" value={d.week} onChange={(e) => setWeek(e.target.value)}
            className="px-2.5 py-1.5 rounded-md border border-zinc-300 bg-white text-sm">
            {d.weken.map((w, i) => <option key={w} value={w}>{i === 0 ? `Deze week (${Number(w.slice(-2))})` : `Week ${Number(w.slice(-2))}`}</option>)}
          </select>
        </div>
      </div>
      {!d.bewerkbaar && <p data-testid="overleg-alleen-lezen" className="mb-3 text-xs text-zinc-500">Een eerder overleg. Je kunt het lezen, niet meer aanpassen.</p>}
      <div className="grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-4">
        <div>
          <SectieKop titel={d.week === d.weken[0] ? "Afspraken deze week" : `Afspraken week ${Number(d.week.slice(-2))}`} aantal={d.afspraken.length} hint="nagekomen · niet · loopt" />
          {d.afspraken.length ? (
            <ul className={LIJST} data-testid="overleg-afspraken">
              {d.afspraken.map((a) => <OverlegAfspraak key={a.id} a={a} besproken={d.besproken.includes(a.id)} bewerkbaar={d.bewerkbaar}
                onActie={(x, soort) => (soort === "loopt" ? loopt(x) : onActie(x, soort, d.week))} />)}
            </ul>
          ) : <div className={`${KAART} px-3 py-4 text-sm text-zinc-400`} data-testid="overleg-geen-afspraken">Geen lopende afspraken.</div>}
          {d.bewerkbaar && (
            <button type="button" data-testid="overleg-nieuwe-afspraak" onClick={onNieuw}
              className="mt-3 flex items-center gap-1.5 px-4 py-2 rounded-md bg-zinc-950 text-white text-sm font-semibold hover:bg-zinc-800 transition-colors">
              <Plus className="h-4 w-4" /> Nieuwe afspraak
            </button>
          )}
        </div>
        <div>
          <SectieKop titel={`Vaste vragen ${d.naam}`} aantal={vragen.length} hint="in Instellingen → Afdelingen" />
          <ul className={`${LIJST} mb-3 md:mb-4`} data-testid="overleg-vragen">
            {vragen.length === 0 && <li className="px-3 py-3 text-sm text-zinc-400">{d.bewerkbaar ? "Nog geen vaste vragen." : "Geen antwoorden opgeslagen."}</li>}
            {vragen.map((v) => (
              <li key={v.id} className="px-3 py-2.5 text-[13px] text-zinc-800">
                {v.tekst}{v.lezen && d.bewerkbaar && <span className="ml-1.5 text-[11px] text-zinc-400">(vraag niet meer in gebruik)</span>}
                {!v.lezen ? (
                  <AutoVeld key={`${veldKey}-${v.id}`} veldId={`vraag-${v.id}`} pad={pad} body={(t) => ({ week: d.week, vragen: { [v.id]: t } })} waarde={v.antwoord}
                    onStatus={status} testid={`overleg-vraag-${v.id}`} rijen={1} snel placeholder="Kort antwoord" />
                ) : <p data-testid={`overleg-vraag-${v.id}-lezen`} className="mt-1 text-zinc-600">{v.antwoord || "—"}</p>}
              </li>
            ))}
          </ul>
          <SectieKop titel="Gespreksnotitie" />
          <div className={`${KAART} p-3`}>
            {d.bewerkbaar ? (
              <AutoVeld key={`${veldKey}-notitie`} veldId="notitie" pad={pad} body={(t) => ({ week: d.week, notitie: t })} waarde={d.notitie} onStatus={status}
                testid="overleg-notitie" rijen={5} placeholder="Wat is er besproken, wat spraken jullie af?" />
            ) : <p data-testid="overleg-notitie-lezen" className="text-[13px] text-zinc-700 whitespace-pre-line">{d.notitie || "Geen notitie."}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
